import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Tag,
    Table,
    InputNumber,
    Flex,
    Form,
    Spin,
    Button,
    Card,
    Col,
    notification,
    Alert,
    Modal,
    Row,
    Tooltip,
    Typography,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import {
    useGetUserDetailsQuery,
    useGetUserNeighboursQuery,
    useUpdateUserDetailsMutation,
    useGetLiqpayFormMutation,
    useBuyKeychainMutation,
} from "../../services/auth/authService";
import { useDispatch } from "react-redux";
import { userLogout } from "../../features/auth/authActions";
import "dayjs/locale/uk";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";

const { Meta } = Card;
const { Text } = Typography;

const ProfileSubComponent = () => {
    const [modal, modalContext] = Modal.useModal();
    const dispatch = useDispatch();
    const [api, contextHolder] = notification.useNotification();
    dayjs.extend(LocalizedFormat);
    const navigate = useNavigate();
    const {
        data: userInfo = {},
        isFetching: isUserInfoFetching,
        error,
    } = useGetUserDetailsQuery();
    const { data: neighbours = [], isFetching: isNeghboursFetching } =
        useGetUserNeighboursQuery();
    const [getLiqPayForm, { isFetching: isLiqpayLoading, data: liqpayForm }] =
        useGetLiqpayFormMutation();
    const [updateInfo, { isLoading: isUpdateInfoLoading }] =
        useUpdateUserDetailsMutation();
    const [buyKeychain, { isLoading: isBuyKeychainLoading }] =
        useBuyKeychainMutation();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showSaveBtn, setShowSaveBtn] = useState(false);
    const [sum, setSum] = useState(50);
    const openNotification = (text) => {
        api.success({
            message: "Повідомлення",
            description: text ?? "Ваші зміни успішно збережено",
        });
    };
    const openErrorNotification = (text) => {
        api.error({
            message: "Повідомлення",
            description: text ?? "Ваші зміни успішно збережено",
        });
    };
    const profileItems = [
        {
            key: "2",
            label: "Баланс",
            value: `${userInfo.balance ?? 0} грн`,
        },
        {
            key: "3",
            label: "Стан акаунту",
            value: userInfo.blocked ? (
                <Tag color="error">заблоковано</Tag>
            ) : (
                <Tag color="success">активний</Tag>
            ),
        },
    ];
    const profileItemsLoading = [
        {
            key: "2",
            label: "Баланс",
            value: <Spin />,
        },
        {
            key: "3",
            label: "Стан акаунту",
            value: <Spin />,
        },
    ];
    const summaryCards = isUserInfoFetching
        ? profileItemsLoading
        : profileItems;
    const monthlyPayment = selectedRowKeys.length * 50;
    const today = dayjs();
    const nextBillDate =
        today.date() <= 10
            ? today.date(10)
            : today.add(1, "month").date(10);
    const unpaidBillsCount = userInfo.bills?.filter((bill) => !bill.paid).length || 0;
    const columns = [
        {
            title: "Ім'я",
            dataIndex: "first_name",
            key: "first_name",
            width: 10,
        },
        {
            title: "Моб. Телефон",
            dataIndex: "phone",
            key: "phone",
            width: 10,
        },
        {
            title: "Баланс",
            dataIndex: "balance",
            key: "balance",
            width: 10,
        },
        {
            title: "Стан акаунту",
            key: "status",
            dataIndex: "status",
            width: 1,
            render: (_, record) =>
                record.blocked ? (
                    <Tag color="error">неактивн</Tag>
                ) : (
                    <Tag color="success">активний</Tag>
                ),
        },
        {
            title: "Дії",
            key: "actions",
            width: 1,
            render: (_, record) => {
                if (record.phone === userInfo.phone) {
                    return "-";
                }
                const isPayingForNeighbour = (
                    userInfo.paying_for || []
                ).includes(record.phone);
                return (
                    <Button
                        type="link"
                        danger
                        size="small"
                        disabled={isPayingForNeighbour}
                        onClick={() => handleHideNeighbour(record.phone)}
                    >
                        Приховати
                    </Button>
                );
            },
        },
    ];
    const handleHideNeighbour = (phone) => {
        if (!phone || phone === userInfo.phone) {
            return;
        }
        const currentPayingFor = userInfo.paying_for || [];
        if (currentPayingFor.includes(phone)) {
            openErrorNotification(
                "Неможливо приховати сусіда, за якого ви платите",
            );
            return;
        }

        updateInfo({
            hide_neighbour_phone: phone,
            phone: userInfo.phone,
            address: userInfo.address,
            flat: userInfo.flat,
            keys_count: userInfo.keys_count,
        })
            .unwrap()
            .then(() => {
                setSelectedRowKeys(currentPayingFor);
                setShowSaveBtn(false);
                openNotification("Сусіда приховано");
            })
            .catch((rejected) => {
                openErrorNotification(
                    Object.values(rejected?.data || {}).join("") ||
                        "Не вдалося приховати сусіда",
                );
            });
    };
    const rowSelection = {
        onChange: (selectedRowKeys) => {
            setSelectedRowKeys(selectedRowKeys);
            if (userInfo.paying_for !== null) {
                let diff = selectedRowKeys.filter(
                    (x) => !userInfo.paying_for.includes(x),
                );
                let diffR = userInfo.paying_for.filter(
                    (x) => !selectedRowKeys.includes(x),
                );
                setShowSaveBtn(diff.length > 0 || diffR.length > 0);
            } else {
                setShowSaveBtn(true);
            }
        },
    };
    const onSumChange = (value) => {
        setSum(value);
    };
    useEffect(() => {
        if (error?.status === 401) {
            dispatch(userLogout()).then(() => navigate("/sign-in"));
        }
    }, [dispatch, navigate, error]);
    useEffect(() => {
        if (sum > 0) {
            getLiqPayForm(sum);
        }
    }, [sum, getLiqPayForm]);
    useEffect(() => {
        setSelectedRowKeys(userInfo.paying_for ? userInfo.paying_for : []);
        setShowSaveBtn(false);
    }, [userInfo]);

    return (
        <div className="compact-profile-shell">
            {contextHolder}
            {userInfo.verified === false && (
                <Alert
                    message="Зверніть увагу"
                    description={
                        <div>
                            Ваш номер телефону не підтверджено{" "}
                            <Button
                                type="link"
                                onClick={() => navigate("/verify")}
                            >
                                Перейти на сторінку підтвердження
                            </Button>
                        </div>
                    }
                    type="info"
                />
            )}
            <Card className="compact-section-card compact-summary-merged-card" bordered={false}>
                <div className="compact-profile-stats">
                    {summaryCards.map((item) => (
                        <div className="compact-profile-stat" key={item.key}>
                            <Text className="compact-summary-stat-label">{item.label}</Text>
                            <div className="compact-summary-stat-value">
                                {item.value}
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
            <Card className="compact-section-card compact-forecast-card" bordered={false}>
                <div className="compact-section-head compact-forecast-head">
                    <div>
                        <Text className="compact-section-title">Прогноз платежу</Text>
                        <div className="compact-section-subtitle">
                            Наступний рахунок очікується {nextBillDate.locale("uk").format("D MMMM")}
                        </div>
                    </div>
                </div>
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                        <div className="compact-forecast-metric">
                            <Text className="compact-summary-stat-label">Щомісячно</Text>
                            <div className="compact-forecast-value">{monthlyPayment} грн</div>
                        </div>
                    </Col>
                    <Col xs={24} md={8}>
                        <div className="compact-forecast-metric">
                            <Text className="compact-summary-stat-label">Акаунтів</Text>
                            <div className="compact-forecast-value">{selectedRowKeys.length}</div>
                        </div>
                    </Col>
                    <Col xs={24} md={8}>
                        <div className="compact-forecast-metric">
                            <Text className="compact-summary-stat-label">Неоплачених рахунків</Text>
                            <div className={`compact-forecast-value ${unpaidBillsCount > 0 ? "is-negative" : ""}`}>
                                {unpaidBillsCount}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Card>
            <Card className="compact-section-card" bordered={false}>
                <div className="compact-section-head">
                    <Text className="compact-section-title">
                        {selectedRowKeys.length > 0
                            ? `Ви платите за ${selectedRowKeys.length} акаунти`
                            : "Виберіть акаунти, які бажаєте оплатити"}
                    </Text>
                    <Text className="compact-section-subtitle">
                        {selectedRowKeys.length > 0
                            ? `${selectedRowKeys.length * 50} грн / місяць`
                            : "Кошти розподіляться між вибраними акаунтами"}
                        {" "}
                        <Tooltip title="Поставте галочки біля акаунтів, натисніть 'Зберегти', після чого можна поповнити рахунок і кошти розподіляться між вибраними акаунтами.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Text>
                </div>
                <Table
                    className="compact-table"
                    columns={columns}
                    pagination={false}
                    scroll={{ x: 760 }}
                    rowSelection={{
                        type: "checkbox",
                        selectedRowKeys: selectedRowKeys,
                        ...rowSelection,
                    }}
                    rowKey={(record) => record.phone}
                    dataSource={neighbours}
                    size="small"
                    loading={isNeghboursFetching}
                />
                <div className="compact-actions-row">
                    <Button
                        block
                        disabled={!showSaveBtn}
                        loading={isUpdateInfoLoading}
                        onClick={() =>
                            updateInfo({
                                paying_for: selectedRowKeys,
                                phone: userInfo.phone,
                                address: userInfo.address,
                                flat: userInfo.flat,
                                keys_count: userInfo.keys_count,
                            })
                                .unwrap()
                                .then(() => openNotification())
                        }
                        type="primary"
                    >
                        Зберегти
                    </Button>
                </div>
            </Card>
            {userInfo.paying_for?.length > 0 && (
                <Card
                    className="compact-section-card compact-payment-card profile-topup-card"
                    bordered={false}
                    title={
                        <div className="compact-card-title">Поповнення рахунку</div>
                    }
                >
                    <Row gutter={[16, 16]} align="stretch">
                        <Col xs={24} lg={10}>
                            <div className="parking-topup-balance">
                                <Text className="parking-topup-balance__label">Поточний баланс</Text>
                                <div className="parking-topup-balance__value">
                                    {isUserInfoFetching ? <Spin /> : `${userInfo.balance ?? 0} грн`}
                                </div>
                            </div>
                            <Flex className="parking-topup-presets" gap={8} wrap="wrap">
                                {[100, 200, 500].map((preset) => (
                                    <Button
                                        key={preset}
                                        size="small"
                                        type={sum === preset ? "primary" : "default"}
                                        onClick={() => onSumChange(preset)}
                                    >
                                        {preset} грн
                                    </Button>
                                ))}
                            </Flex>
                            <Form layout="vertical">
                                <Form.Item label="Сума">
                                    <InputNumber
                                        type="number"
                                        min={1}
                                        value={sum}
                                        onChange={(value) => onSumChange(value || 0)}
                                        addonAfter="₴"
                                        controls={false}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </Form>
                        </Col>
                        <Col xs={24} lg={14}>
                            <div className="replenishment-payment-widget">
                                {isLiqpayLoading ? (
                                    <Spin />
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: liqpayForm }} />
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}
            <Card
                className="compact-section-card compact-keychain-card"
                bordered={false}
                cover={
                    <div className="compact-keychain-card__media">
                        <img
                            alt="Брелок доступу"
                            id="brelok-logo"
                            src="/img/brelok.png"
                        />
                    </div>
                }
            >
                <Flex align="center" style={{ flexDirection: "column" }}>
                    <Meta title="Вартість одного брелка 200 грн" />
                    <p />
                    <Button
                        shape="round"
                        style={{
                            backgroundColor: "#6CA91C",
                            color: "white",
                            width: "70%",
                            fontSize: 17,
                            fontWeight: "500",
                        }}
                        size="large"
                        loading={isBuyKeychainLoading}
                        onClick={async () => {
                            const result = await modal.confirm({
                                title: "Ви дійсно бажаєте замовити брелок?",
                                content:
                                    "При підтвердженні з вашого балансу буде списано 200 грн. Згодом брелок можна буде забрати у консьєржа.",
                            });
                            if (result) {
                                buyKeychain()
                                    .unwrap()
                                    .then(() => {
                                        openNotification(
                                            "Ви успішно замовили брелок",
                                        );
                                    })
                                    .catch((rejected) => {
                                        openErrorNotification(
                                            rejected?.data ===
                                                "Insufficient funds"
                                                ? "Недостатньо коштів"
                                                : rejected?.data,
                                        );
                                    });
                            }
                        }}
                    >
                        ЗАМОВИТИ БРЕЛОК
                    </Button>
                </Flex>
            </Card>

            {modalContext}
        </div>
    );
};

export default ProfileSubComponent;
